import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getTreatmentReportInit } from '@/services/main/treatmentReport';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';

export const useTreatmentReportInit = (
  patientId?: string,
  options?: {
    enabled?: boolean;
    treatmentYearmonth?: number;
    filterByHeaderServiceType?: boolean;
    excludeExpiredCertificate?: boolean;
  }
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [
      'treatment-reports',
      'init',
      tenant,
      clinicNumber,
      patientId,
      serviceType,
      options?.treatmentYearmonth,
    ],
    queryFn: () =>
      getTreatmentReportInit(
        tenant! || '',
        clinicNumber! || '',
        patientId!,
        serviceType,
        {
          treatmentYearmonth: options?.treatmentYearmonth,
          filterByHeaderServiceType: options?.filterByHeaderServiceType,
          excludeExpiredCertificate: options?.excludeExpiredCertificate,
        }
      ),
    enabled: (options?.enabled ?? true) && !!tenant && !!clinicNumber && !!patientId,
    staleTime: 60_000,
    placeholderData: previousData => previousData,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading: isLoading && !data, isFetching, isError };
};
